<template>
  <section
    v-if="!isFetching"
    :class="[
      'collections-list',
      !isSkillDiveB2BUser && 'collections-list--skill-dive-banner',
    ]"
  >
    <div class="collections-list__lab-experience-link-wrapper">
      <router-link
        :to="{ name: 'skill-dive' }"
        class="collections-list__lab-experience-link btn btn--tertiary"
        :style="`--btn-icon-size: 16px`"
      >
        <ArrowLeftIcon class="collections-list__lab-experience-link-arrow" />
        Back to Skill Dive
      </router-link>
    </div>
    <div class="collections-list__title">Collections you might like</div>
    <div class="collections-list__text">
      Lab Collections provide a clear pathway to success, allowing you to unlock
      your full potential, bringing together a series of labs that focus on
      specific technologies or concept domains, within learning areas.
    </div>
    <div class="collections-list__search-section">
      <SearchInput
        :viewToggler="false"
        class="collections-list__search-section-input"
      />
      <div class="collections-list__search-section-filters">
        <div class="collections-list__search-section-tags-wrapper">
          <FilterTags
            :currentRefinements="{ 'tags.name': selectedTags }"
            :filterMap="{}"
            showTagsOnly
            @remove-filter="
              filter => toggleAlgoliaFilterAnd(algoliaKeys.searchIndex, filter)
            "
          />
        </div>
        <div class="collections-list__search-section-la-filters">
          <div
            v-for="learningArea in learningAreaFilters"
            :key="learningArea"
            class="collections-list__search-section-la-filters-item"
          >
            <input
              class="collections-list__search-section-la-filters-item-checkbox"
              type="checkbox"
              :checked="
                isAlgoliaFilterOrEnabled(algoliaKeys.searchIndex, {
                  facet: 'learning_areas.name',
                  value: learningArea,
                })
              "
              @change="
                () =>
                  toggleAlgoliaFilterOr(algoliaKeys.searchIndex, {
                    facet: 'learning_areas.name',
                    value: learningArea,
                  })
              "
            />
            <p class="collections-list__search-section-la-filters-item-title">
              {{ learningArea }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <HighlightedFeaturedCollection
      v-show="showHighlightedFeaturedCollection"
      small-view
      class="collections-list__highlighted-featured-collection"
    />
    <div
      :class="['collections-list__list', 'collections-list__list--list-view']"
    >
      <LabsCollectionCard
        v-for="collection in collections"
        :key="collection.objectID"
        :viewStyle="viewStyle"
        :collection="collection"
        :lockedContent="lockedContent(collection)"
        class="labs-experience-featured__cards-item"
        @apply-tag-filter="
          ({ name }) =>
            toggleAlgoliaFilterAnd(algoliaKeys.searchIndex, {
              facet: 'tags.name',
              value: name,
            })
        "
      />
    </div>
    <div v-if="showBrowseMore" class="collections-list__list-more-wrapper">
      <button
        class="collections-list__list-more btn btn--secondary"
        @click="handleBrowseMore"
      >
        Browse more
      </button>
    </div>
    <div
      v-if="!isFetching && (!collections || !collections.length)"
      class="collections-list__list-no-results-wrapper"
    >
      <img
        class="collections-list__list-no-results-wrapper__img"
        src="@/assets/images/status/fail-low.png"
        alt="NO RESULTS"
      />
      <h5 class="collections-list__list-no-results-wrapper__error">
        Oops! We couldn't find any results matching your search.
      </h5>
      <p class="collections-list__list-no-results-wrapper__instruction">
        Please try searching for something else.
      </p>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import ArrowLeft from '@/assets/icons/course/exercise/arrow-left.svg'
import { algoliaSearchMixin, collectionCommons } from '@/helpers/mixins'
import LabsCollectionCard from '@/components/lab-experience/LabsCollectionCard.vue'
import HighlightedFeaturedCollection from '@/components/lab-experience/HighlightedFeaturedCollection.vue'
import SearchInput from '@/components/shared/SearchInput.vue'
import FilterTags from '@/components/shared/FilterTags.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'CollectionsList',
  components: {
    SearchInput,
    LabsCollectionCard,
    HighlightedFeaturedCollection,
    ArrowLeftIcon: { ...ArrowLeft, name: 'ArrowLeft' },
    FilterTags,
  },
  mixins: [algoliaSearchMixin, collectionCommons],
  inject: ['getWindowWidth'],
  data() {
    return {
      isFetching: false,
      lazyLoad: false,
      viewStyle: 'list',
      learningAreaFilters: CONSTANTS.LAB_EXPERIENCE.LEARNING_AREAS.map(
        la => la.name
      ),
    }
  },
  computed: {
    ...mapState({
      algoliaKeys: state => state.search.algoliaKeys,
      availablePasses: state => state.auth.available_passes,
    }),
    ...mapGetters({
      isLabExperienceUser: 'auth/isLabExperienceUser',
      isSkillDiveB2BUser: 'auth/isSkillDiveB2BUser',
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
      isSkillDiveB2CNetworking: 'auth/isSkillDiveB2CNetworking',
      isSkillDiveB2CCloud: 'auth/isSkillDiveB2CCloud',
      isSkillDiveB2CCyberSecurity: 'auth/isSkillDiveB2CCyberSecurity',
      labExperienceEnhance: 'app/labExperienceEnhance',
      isBusinessUser: 'business/isBusinessUser',
      businessUser: 'business/getBusinessUser',
      showSkillDive: 'labExperience/showSkillDive',
    }),
    algoliaHelper() {
      return this.algoliaHelpers[this.algoliaKeys.searchIndex]
    },
    collections() {
      return this.getIndexHits(this.algoliaKeys.searchIndex).filter(
        collection =>
          collection.objectID !== CONSTANTS.LABS_COLLECTIONS.VULNERABILITIES
      )
    },
    selectedTags() {
      return this.algoliaHelper?.state?.facetsRefinements['tags.name'] || []
    },
    isSmallScreen() {
      return this.getWindowWidth() < 1300
    },
    showBrowseMore() {
      const hitsPerPage = this.algoliaHelper?.state.hitsPerPage
      const nbHits = this.algoliaHelper?.lastResults?.nbHits
      return nbHits > hitsPerPage && !this.lazyLoad
    },
    showHighlightedFeaturedCollection() {
      const learningAreaRefinements = this.algoliaHelper?.state
        .disjunctiveFacetsRefinements['learning_areas.name']

      if (learningAreaRefinements && learningAreaRefinements.length) {
        return learningAreaRefinements.includes('Cyber Security')
      } else return true
    },
  },
  async created() {
    this.cleanCurrentCollection()

    const {
      algoliaKeys: { searchIndex },
    } = this
    const algoliaHelper = await this.initializeAlgoliaSearch(
      searchIndex,
      {
        facets: ['content_type', 'tags.name'],
        disjunctiveFacets: ['learning_areas.name'],
        hitsPerPage: 12,
      },
      {
        useUrl: true,
        useDebounceSearchQuery: true,
        hiddenFacets: ['content_type'],
      }
    )

    algoliaHelper.addFacetRefinement('content_type', 'collection')

    if (this.isSkillDiveB2BUser) {
      algoliaHelper
        .addDisjunctiveFacetRefinement('learning_areas.name', 'Cyber Security')
        .addDisjunctiveFacetRefinement('learning_areas.name', 'Networking')
        .addDisjunctiveFacetRefinement('learning_areas.name', 'Cloud')
    } else if (this.isSkillDiveB2CUser) {
      if (this.isSkillDiveB2CCyberSecurity)
        algoliaHelper.addDisjunctiveFacetRefinement(
          'learning_areas.name',
          'Cyber Security'
        )
      if (this.isSkillDiveB2CNetworking)
        algoliaHelper.addDisjunctiveFacetRefinement(
          'learning_areas.name',
          'Networking'
        )
      if (this.isSkillDiveB2CCloud)
        algoliaHelper.addDisjunctiveFacetRefinement(
          'learning_areas.name',
          'Cloud'
        )
    }
    algoliaHelper.search()
  },
  async beforeMount() {
    if (!this.showSkillDive) {
      this.$router.push({ name: 'home' })
    }
  },
  methods: {
    ...mapActions('labExperience', ['cleanCurrentCollection']),
    handleBrowseMore() {
      const searchIndex = this.algoliaKeys.searchIndex
      this.lazyLoad = true
      this.handleAlgoliaLoadMore(searchIndex)
      this.handleAlgoliaLazyLoad(searchIndex)
    },
  },
}
</script>

<style lang="scss">
.collections-list {
  padding: 100px 20px;
  color: var(--white);
  text-align: center;
  display: flex;
  flex-flow: column;
  align-items: center;
  background-image: url('../assets/images/backgrounds/dashboard_bg.jpg');
  background-size: cover;
  @include tablet {
    padding: 120px 100px;
  }
  @include desktop {
    padding: 125px 80px 150px 100px;
  }
  &--skill-dive-banner {
    padding-top: 120px;

    @include tablet {
      padding-top: 180px;
    }
  }
  > div {
    width: 100%;
    max-width: 1240px;
  }
  &__lab-experience-link {
    &-wrapper {
      display: none;

      @include desktop {
        display: block;
        width: 100%;
        text-align: left;
      }
    }
  }
  &__title {
    font-size: rem(32px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(38px);

    @include desktop {
      font-size: rem(40px);
      font-style: normal;
      font-weight: 700;
      line-height: rem(52px);
      margin-top: 50px;
    }
  }
  &__text {
    color: var(--athens-gray);
    font-size: rem(14px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(20px);
    text-align: left;
    margin: 10px 0 50px;

    @include desktop {
      font-size: rem(16px);
      line-height: rem(24px);
      margin: 5px auto 50px auto;
      width: 75%;
    }
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 50px;
    transition: 1s all ease;

    @include mobile {
      gap: 20px;
    }

    @include desktop {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 15px;

      &--list-view {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
    }

    &-more {
      width: 100%;

      @include mobile {
        width: auto;
      }
    }

    &-no-results-wrapper {
      display: flex;
      align-items: center;
      flex-flow: column;
      padding: 20px;
      min-height: 100vh;

      @include desktop {
        grid-row: 4;
        grid-column: 1 / 3;
      }
      &__img {
        width: 280px;
      }
      &__error {
        margin: -30px 0 0 0;
        color: var(--white);
        font-family: var(--font-bold);
        font-style: normal;
        font-weight: 700;
        font-size: rem(20px);
        line-height: rem(26px);
      }
      &__instruction {
        color: var(--light-blue-gray);
        font-family: var(--font-book);
        font-style: normal;
        font-weight: 450;
        font-size: rem(16px);
        line-height: rem(24px);
      }
    }
  }
  &__list-more-wrapper {
    text-align: left;
  }
  &__search-section {
    &-input {
      margin-bottom: 5px;
    }
    &-filters {
      margin-top: 20px;
      margin-bottom: 50px;
      .filter-tags__tag-list {
        margin: 0;
        margin-bottom: 20px;
      }
    }
    &-la-filters {
      display: none;
      @include desktop {
        display: flex;
        column-gap: 20px;
      }
      &-item {
        display: flex;
        align-items: center;
        padding: 8px 0;
        cursor: pointer;
      }
      &-item-checkbox {
        border: 1px solid var(--mid-gray-dark);
        border-radius: 8px;
        appearance: none;
        background: none;
        color: var(--white);
        padding: 11px;
        &:hover:not([disabled]) {
          border: 1px solid var(--white);
          cursor: pointer;
        }
        &:checked {
          border: 1px solid var(--white);
          background: var(--mid-gray-dark-25);
          color: var(--white);
          position: relative;
          display: inline-block;
          @include tablet {
            width: 22px;
            height: 22px;
          }
          &::before {
            transform: rotate(40deg);
            content: '';
            position: absolute;
            width: 1px;
            height: 9px;
            background-color: var(--white);
            left: 12px;
            top: 6px;
          }
          &::after {
            transform: rotate(-45deg);
            content: '';
            position: absolute;
            width: 1px;
            height: 4px;
            background-color: var(--white);
            left: 8px;
            top: 10px;
          }
        }
      }
      &-item-title {
        font-size: 1rem;
        margin: 0 0 0 5px;
        color: var(--white);
        padding: 0;
      }
    }
  }
  &__highlighted-featured-collection {
    margin-bottom: 20px;
  }
}
</style>
